import * as React from 'react';

import {useSelector} from '@xstate/react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {Card} from '@spaced-out/ui-design-system/lib/components/Card';
import {generateTestId} from '@spaced-out/ui-design-system/lib/utils/qa';

import {RootMachineContext} from '../../machines/rootMachine';
import {useBookingMachine} from '../../hooks/useBookingMachine';
import {selectBookingInfo} from '../../selectors/booking-selector';
import {selectBrandSettings} from '../../selectors/brandSelectors';

import {TEST_ID} from '../../utils/testId';
import {Loader} from '../../lib/loader/loader';
import {BookingForm} from '../booking-form/booking-form';
import {BookingInfo} from '../../lib/booking-info/booking-info';
import {UnavailableTimeSlot} from '../unavailable-timeslot/unavailable-timeslot';
import {BookingDateTimePicker} from '../booking-date-time-picker/booking-date-time-picker';
import {BookingNotifyHostForm} from '../booking-notify-host-form/booking-notify-host-form';
import {BookingRescheduleForm} from '../booking-reschedule/booking-reschedule-form';
import {BookingAvailabilityRangeError} from '../booking-errors/Booking-availability-range-error';
import {RequestMoreAvailabilityForm} from '../request-more-availability/request-more-availability-form';
import {SlotRequestAlreadyExistsError} from '../request-more-availability/slot-request-already-exists-error';
import {SequentialEventEditNotAllowed} from '../sequential-event-edit-not-allowed/sequential-event-edit-not-allowed';
import {RequestMoreAvailabilitySuccess} from '../request-more-availability/request-more-availability-success';

import css from './booking-layout.css';


export const BookingLayout = () => {
  const {t, makeKey, language} = useLabelTranslation();

  const {bookingActor, bookingState} = useBookingMachine();
  const rootState = RootMachineContext.useSelector((state) => state);
  const brandSettings = RootMachineContext.useSelector(selectBrandSettings);
  const bookingInfo = useSelector(bookingActor, (snapshot) =>
    selectBookingInfo(snapshot, t, language, makeKey),
  );

  const brandLogo = brandSettings?.logo || '';
  const bookingInfoItems = bookingInfo.bookingInfoItems;

  const isLoadingAvailabilityRange = bookingState.matches(
    'availabilityRangeLoading',
  );
  const hasUnavailableTimeSlot = bookingState.matches({
    bookingForm: 'unavailableTimeSlot',
  });
  const isBookingFormState = bookingState.matches('bookingForm');
  const isDateTimePickerState = bookingState.matches('dateTimePicker');
  const isNotifyHostState = bookingState.matches('notify');
  const isRescheduleReasonFormState = bookingState.matches('rescheduleForm');
  const isRequestMoreAvailabilityFormState = bookingState.matches(
    'requestMoreAvailabilityForm',
  );
  const hasUnavailableTimeSlotInRescheduleFlow = bookingState.matches({
    rescheduleForm: 'unavailableTimeSlotForReschedule',
  });

  const isRescheduleFlow = rootState.matches('rescheduleFlow');

  const hasUnavailableTimeSlotForReschedule = bookingState.matches({
    rescheduleForm: 'unavailableTimeSlotForReschedule',
  });
  const hasSlotRequestAlreadyExists = bookingState.matches(
    'slotRequestAlreadyExists',
  );

  const hasAvailabilityRangeError = bookingState.matches(
    'availabilityRangeError',
  );
  const rescheduleCancellationNotAllowed = bookingState.matches(
    'rescheduleCancellationNotAllowed',
  );
  const isRequestMoreAvailabilitySuccess = bookingState.matches(
    'requestMoreAvailabilitySuccess',
  );

  return (
    <Card
      classNames={{wrapper: css.bookingLayoutContainer}}
      testId={generateTestId({base: TEST_ID.BOOKING_FLOW, slot: 'container'})}
    >
      <div className={css.bookingInfoLeftContainer}>
        <BookingInfo
          title={bookingInfo.title}
          brandLogo={brandLogo}
          bookingInfoItems={bookingInfoItems}
        />
      </div>
      <div className={css.bookingLayoutDivider} />
      <div className={css.bookingInfoRightContainer}>
        {isLoadingAvailabilityRange && <Loader />}
        {hasUnavailableTimeSlot && (
          <UnavailableTimeSlot isRescheduleFlow={false} />
        )}
        {hasUnavailableTimeSlotForReschedule && (
          <UnavailableTimeSlot isRescheduleFlow={isRescheduleFlow} />
        )}
        {hasSlotRequestAlreadyExists && <SlotRequestAlreadyExistsError />}
        {rescheduleCancellationNotAllowed && <SequentialEventEditNotAllowed />}
        {hasAvailabilityRangeError && <BookingAvailabilityRangeError />}
        {isBookingFormState && !hasUnavailableTimeSlot && <BookingForm />}
        {isRequestMoreAvailabilityFormState && <RequestMoreAvailabilityForm />}
        {isRescheduleReasonFormState &&
          !hasUnavailableTimeSlotInRescheduleFlow && <BookingRescheduleForm />}
        {isDateTimePickerState && <BookingDateTimePicker />}
        {isNotifyHostState && <BookingNotifyHostForm />}
        {isRequestMoreAvailabilitySuccess && <RequestMoreAvailabilitySuccess />}
      </div>
    </Card>
  );
};
